import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { GroupMenu, GroupMenuDto } from '../../../../core/models/system-settings/group-menus.model';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperGroupMenusService } from '../../../../core/mappers/system-settings/mapper-group-menus.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ModalGroupMenuComponent } from '../../modals/system-settings/modal-group-menu/modal-group-menu.component';
import { forkJoin, lastValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-group-menus',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalGroupMenuComponent],
  templateUrl: './group-menus.component.html',
  styleUrl: './group-menus.component.css'
})
export class GroupMenusComponent implements OnInit {
  groupMenus = signal<GroupMenu[]>([]);
  isLoading = signal<boolean>(false);
  isDashboardOpen = signal<boolean>(true);
  Math = Math;

  // Search and Filters
  searchText = signal<string>('');

  // Modal State
  isModalOpen = signal<boolean>(false);
  selectedItem = signal<GroupMenu | null>(null);

  // Import / Export State
  isExporting = signal<boolean>(false);
  isImportModalOpen = signal<boolean>(false);
  importMode = signal<'skip' | 'update' | 'force'>('skip');
  pendingImportData = signal<any>(null);
  selectedFileName = signal<string>('');
  importSummary = signal<{ groupMenusCount: number; groupSubMenusCount: number; menuListsCount: number }>({
    groupMenusCount: 0,
    groupSubMenusCount: 0,
    menuListsCount: 0
  });
  importProgress = signal<{ current: number; total: number; text: string }>({
    current: 0,
    total: 0,
    text: ''
  });

  // Dashboard Stats
  dashboardStats = signal<any[]>([]);

  // Pagination
  currentPage = signal<number>(1);
  pageSize = signal<number>(20);
  pageSizeOptions = [20, 50, 100];

  // Sorting
  sortColumn = signal<string>('number');
  sortDirection = signal<'asc' | 'desc'>('asc');

  // Injected dependencies
  private cookieService = inject(CookieService);
  private router = inject(Router);
  private permissionService = inject(MenuPermissionService);

  // Computed signals for filtering and pagination (Resolving NG0600)
  filteredData = computed(() => {
    let filtered = this.groupMenus();
    const search = this.searchText().trim().toLowerCase();

    if (search) {
      filtered = filtered.filter(m =>
        m.name.toLowerCase().includes(search) ||
        m.id.toString() === search
      );
    }

    const col = this.sortColumn();
    const dir = this.sortDirection();
    if (col) {
      filtered = [...filtered].sort((a: any, b: any) => {
        let valA = a[col];
        let valB = b[col];

        if (typeof valA === 'string' && typeof valB === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }

        if (valA < valB) return dir === 'asc' ? -1 : 1;
        if (valA > valB) return dir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  });

  totalItems = computed(() => this.filteredData().length);

  filteredAndPaginatedData = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredData().slice(start, start + this.pageSize());
  });

  // ================= Normal Functions =================

  constructor(
    private apiService: ApiSystemSettingsService,
    private mapper: MapperGroupMenusService,
    private toast: ToastService
  ) { }

  ngOnInit() {
    this.getGroupMenus();
  }

  /**
   * Toggles the stats dashboard visibility.
   */
  toggleDashboard(): void {
    this.isDashboardOpen.set(!this.isDashboardOpen());
  }

  /**
   * Re-fetches the group menus table from API.
   */
  onRefresh(): void {
    this.searchText.set('');
    this.currentPage.set(1);
    this.getGroupMenus();
  }

  /**
   * Calculates KPI stats for the top cards.
   */
  private calculateDashboardStats(): void {
    const menus = this.groupMenus();
    const total = menus.length;
    const activeTotal = menus.filter(m => m.status).length;
    const inactiveTotal = total - activeTotal;

    this.dashboardStats.set([
      { title: 'กลุ่มเมนูทั้งหมด', value: total.toLocaleString(), icon: 'folder', color: 'text-amber-500' },
      { title: 'เปิดใช้งาน', value: activeTotal.toLocaleString(), icon: 'check_circle', color: 'text-emerald-500' },
      { title: 'ปิดใช้งาน', value: inactiveTotal.toLocaleString(), icon: 'cancel', color: 'text-rose-500' }
    ]);
  }

  /**
   * Triggered on search input submit.
   */
  public onSearch(): void {
    this.currentPage.set(1);
  }

  /**
   * Sorts data by a specific column.
   * @param column Target column name
   */
  public sortData(column: string): void {
    if (this.sortColumn() === column) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }
  }

  /**
   * Form opens a modal for creating a new group menu.
   */
  public navigateToCreate(): void {
    this.selectedItem.set(null);
    this.isModalOpen.set(true);
  }

  /**
   * Form opens a modal for editing a group menu.
   * @param item Selected group menu model
   */
  public navigateToEdit(item: GroupMenu): void {
    this.selectedItem.set(item);
    this.isModalOpen.set(true);
  }

  /**
   * Closes the edit/create modal dialog.
   */
  public closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedItem.set(null);
  }

  /**
   * Reads and parses uploaded JSON file for menu structure import.
   * @param event HTML File Input event
   */
  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.selectedFileName.set(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const parsed = JSON.parse(text);

        let hierarchy: any[] = [];
        if (parsed.hierarchy && Array.isArray(parsed.hierarchy)) {
          hierarchy = parsed.hierarchy;
        } else if (parsed.groupMenus && Array.isArray(parsed.groupMenus)) {
          hierarchy = parsed.groupMenus;
        } else if (Array.isArray(parsed)) {
          hierarchy = parsed;
        }

        if (hierarchy.length === 0) {
          this.toast.error('ไม่พบโครงสร้างข้อมูลเมนูในไฟล์ JSON');
          input.value = '';
          return;
        }

        let gCount = hierarchy.length;
        let subCount = 0;
        let menuCount = 0;

        hierarchy.forEach((g: any) => {
          if (g.subMenus && Array.isArray(g.subMenus)) {
            subCount += g.subMenus.length;
            g.subMenus.forEach((s: any) => {
              if (s.menuLists && Array.isArray(s.menuLists)) {
                menuCount += s.menuLists.length;
              }
            });
          }
          if (g.menuLists && Array.isArray(g.menuLists)) {
            menuCount += g.menuLists.length;
          }
        });

        this.pendingImportData.set({ hierarchy, original: parsed });
        this.importSummary.set({
          groupMenusCount: gCount,
          groupSubMenusCount: subCount,
          menuListsCount: menuCount
        });
        this.isImportModalOpen.set(true);
      } catch (err) {
        console.error(err);
        this.toast.error('ไฟล์ JSON ไม่ถูกต้อง กรุณาตรวจสอบรูปแบบไฟล์');
      } finally {
        input.value = '';
      }
    };
    reader.readAsText(file);
  }

  /**
   * Closes the menu import modal.
   */
  public closeImportModal(): void {
    this.isImportModalOpen.set(false);
    this.pendingImportData.set(null);
    this.selectedFileName.set('');
    this.importProgress.set({ current: 0, total: 0, text: '' });
  }

  // --- Pagination Helpers ---

  get totalPages(): number {
    return Math.ceil(this.totalItems() / (this.pageSize() || 1));
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  /**
   * Sets current page.
   * @param page Target page index
   */
  public setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
    }
  }

  /**
   * Updates items count per page.
   * @param event Page size select element change event
   */
  public onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
  }

  /**
   * Track by function for ngFor.
   */
  public trackById(index: number, item: GroupMenu): number {
    return item.id;
  }

  // ================= API Functions =================

  /**
   * Fetches group menus list from API and maps them to UI model.
   */
  getGroupMenus(): void {
    this.isLoading.set(true);
    this.apiService.getGroupMenusForTable().subscribe({
      next: (dtos: GroupMenuDto[]) => {
        console.log("Get GroupMenus : ", dtos);
        try {
          const list = (dtos || []).map((d: GroupMenuDto) => this.mapper.mapGroupMenuFromDto(d));
          this.groupMenus.set(list);
          this.calculateDashboardStats();
        } catch (error) {
          this.toast.error('โครงสร้างข้อมูลไม่ถูกต้อง');
        } finally {
          this.isLoading.set(false);
        }
      },
      error: (err: any) => {
        console.error(err);
        const fallback = [
          { id: 1, name: 'HOME', status: true, number: 1, createDate: new Date(), writeDate: new Date() },
          { id: 2, name: 'PARKING SYSTEM', status: true, number: 2, createDate: new Date(), writeDate: new Date() },
          { id: 3, name: 'SETTINGS', status: true, number: 3, createDate: new Date(), writeDate: new Date() },
          { id: 4, name: 'อื่นๆ', status: true, number: 4, createDate: new Date(), writeDate: new Date() }
        ];
        this.groupMenus.set(fallback);
        this.calculateDashboardStats();
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Exports full menu structure (Group Menus, Group Sub Menus, Menu Lists) as a JSON file.
   */
  public exportMenuData(): void {
    this.isExporting.set(true);
    this.isLoading.set(true);

    forkJoin([
      this.apiService.getGroupMenusForTable().pipe(catchError(() => of([]))),
      this.apiService.getGroupSubMenusForTable().pipe(catchError(() => of([]))),
      this.apiService.getMenuListsForTable().pipe(catchError(() => of([])))
    ]).subscribe({
      next: ([groupDtos, subDtos, menuDtos]) => {
        const groupMap = new Map<number, any>();
        (groupDtos || []).forEach(g => {
          groupMap.set(g.id, {
            name: g.name,
            status: g.status ?? true,
            number: g.number || 1,
            subMenus: [],
            menuLists: []
          });
        });

        const subMap = new Map<number, any>();
        (subDtos || []).forEach(s => {
          const subObj = {
            name: s.name,
            enable: s.enable ?? true,
            number: s.number || 1,
            icon: s.icon || 'folder',
            menuLists: []
          };
          subMap.set(s.id, subObj);

          if (s.groupMenuId && groupMap.has(s.groupMenuId)) {
            groupMap.get(s.groupMenuId).subMenus.push(subObj);
          }
        });

        (menuDtos || []).forEach(m => {
          const menuObj = {
            name: m.name,
            status: m.status ?? true,
            path: m.path || '',
            pathId: m.pathId ?? null,
            number: m.number || 1
          };

          if (m.groupSubId && subMap.has(m.groupSubId)) {
            subMap.get(m.groupSubId).menuLists.push(menuObj);
          } else if (m.groupMenuId && groupMap.has(m.groupMenuId)) {
            groupMap.get(m.groupMenuId).menuLists.push(menuObj);
          }
        });

        const hierarchy = Array.from(groupMap.values());

        const exportData = {
          exportVersion: '1.0',
          exportedAt: new Date().toISOString(),
          summary: {
            groupMenusCount: groupDtos.length,
            groupSubMenusCount: subDtos.length,
            menuListsCount: menuDtos.length
          },
          hierarchy: hierarchy,
          raw: {
            groupMenus: groupDtos,
            groupSubMenus: subDtos,
            menuLists: menuDtos
          }
        };

        const jsonStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const now = new Date();
        const dateStr = now.toISOString().replace(/[-:T.]/g, '').slice(0, 14);

        a.href = url;
        a.download = `city_parking_menus_export_${dateStr}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        this.toast.success('ส่งออกข้อมูลเมนูสำเร็จแล้ว');
        this.isLoading.set(false);
        this.isExporting.set(false);
      },
      error: (err) => {
        console.error(err);
        this.toast.error('เกิดข้อผิดพลาดในการดึงข้อมูลเมนูเพื่อส่งออก');
        this.isLoading.set(false);
        this.isExporting.set(false);
      }
    });
  }

  /**
   * Imports menu hierarchy via single API upsert (pathId / name). Does not touch roles.
   */
  public async importMenuData(): Promise<void> {
    const importPayload = this.pendingImportData();
    if (!importPayload || !importPayload.hierarchy) return;

    this.isLoading.set(true);

    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;

    if (userId <= 0 || menuId <= 0) {
      this.toast.error('ไม่พบ userId หรือ menuId สำหรับตรวจสิทธิ์');
      this.isLoading.set(false);
      return;
    }

    const hierarchy: any[] = importPayload.hierarchy;
    this.importProgress.set({ current: 0, total: 1, text: 'กำลังนำเข้าโครงสร้างเมนู...' });

    try {
      const res: any = await lastValueFrom(
        this.apiService.importMenuStructure({
          mode: this.importMode(),
          userId,
          menuId,
          hierarchy,
        })
      );

      this.importProgress.set({ current: 1, total: 1, text: 'นำเข้าเสร็จแล้ว' });

      if (res?.isSuccess) {
        const created = res.menusCreated ?? 0;
        const updated = res.menusUpdated ?? 0;
        const skipped = res.menusSkipped ?? 0;
        this.toast.success(
          res.message || `นำเข้าสำเร็จ (สร้าง ${created}, อัปเดต ${updated}, ข้าม ${skipped})`
        );
        if (Array.isArray(res.warnings) && res.warnings.length > 0) {
          console.warn('importMenuStructure warnings:', res.warnings);
        }
        this.closeImportModal();
        this.onRefresh();
      } else {
        this.toast.error(res?.message || 'นำเข้าข้อมูลเมนูไม่สำเร็จ');
      }
    } catch (err) {
      console.error(err);
      this.toast.error('เกิดข้อผิดพลาดในการนำเข้าข้อมูลเมนู');
    } finally {
      this.isLoading.set(false);
    }
  }
}

